import { commonUtils } from '@/helpers/commonUtils'
import PdfPage, { PageHeader } from '../PdfPage/PdfPage'
import styles from './PdfPage7.module.less'
import CoreChart, { useChartConfig } from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoAnalysisChart/components/CoreChart/CoreChart'
import LegendDot from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoAnalysisChart/components/LegendDot/LegendDot'
import VideoAnalysisChartCover from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoAnalysisChart/components/VideoAnalysisChartCover/VideoAnalysisChartCover'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'
interface Props {
  project: ProjectModels.ProjectDetail
  onRendered: (cnt: number) => void
  no: number
}

function normalizeSplashProject(project: ProjectModels.ProjectDetail) {
  const durationValue = Number(project.adDuration)
  const safeDurationSecondCount =
    Number.isFinite(durationValue) && durationValue > 0 ? Math.ceil(durationValue) : 0
  const maxMetricSecond = project.detailMetrics.reduce((max, item) => {
    return Number.isFinite(item.secondCount) ? Math.max(max, item.secondCount) : max
  }, 0)
  const maxFrameSecond = project.frameInfos.reduce((max, item) => {
    const second = Number(item.secondCount)
    return Number.isFinite(second) ? Math.max(max, second) : max
  }, 0)
  const targetSecondCount = Math.min(
    10,
    Math.max(
      5,
      project.detailMetrics.length,
      project.frameInfos.length,
      maxMetricSecond,
      maxFrameSecond,
      safeDurationSecondCount
    )
  )
  const metricsBySecond = new Map(project.detailMetrics.map((item) => [item.secondCount, item]))
  const frameInfosBySecond = new Map(
    project.frameInfos.map((item) => [Number(item.secondCount), item])
  )
  const fallbackMetric = project.detailMetrics[project.detailMetrics.length - 1]
  const fallbackFrameInfo = project.frameInfos[project.frameInfos.length - 1]

  if (!fallbackMetric || !fallbackFrameInfo || targetSecondCount <= project.detailMetrics.length) {
    return project
  }

  const detailMetrics = Array.from({ length: targetSecondCount }, (_, index) => {
    const second = index + 1
    const metric = metricsBySecond.get(second)
    if (metric) {
      return metric
    }
    const prevMetric = metricsBySecond.get(second - 1) || fallbackMetric
    return {
      ...prevMetric,
      secondCount: second,
    }
  })

  const frameInfos = Array.from({ length: targetSecondCount }, (_, index) => {
    const second = index + 1
    const frameInfo = frameInfosBySecond.get(second)
    if (frameInfo) {
      return frameInfo
    }
    const prevFrameInfo = frameInfosBySecond.get(second - 1) || fallbackFrameInfo
    return {
      ...prevFrameInfo,
      secondCount: String(second),
    }
  })

  return {
    ...project,
    detailMetrics,
    frameInfos,
  }
}

function PdfPage7({ project, onRendered, no }: Props) {
  const { t } = useTranslation()
  const isPortraitLayout = isPortraitByProjectInfo(project)
  const adDuration = Number(project.adDuration || 0)
  const inferredSecondCount = Math.max(
    project.detailMetrics.length,
    project.frameInfos.length,
    Number.isFinite(adDuration) && adDuration > 0 ? Math.ceil(adDuration) : 0
  )
  const isPortraitSplashCreative =
    isPortraitLayout &&
    (project.durationCategory === 'five_to_ten' ||
      (project.durationCategory !== 'ten_plus' &&
        inferredSecondCount > 0 &&
        inferredSecondCount <= 10))
  const chartProject = useMemo(() => {
    if (!isPortraitSplashCreative) {
      return project
    }
    return normalizeSplashProject(project)
  }, [isPortraitSplashCreative, project])
  const chartConfig = useChartConfig(chartProject.detailMetrics)
  const chartGrid = chartConfig.config.grid as any
  const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(null)
  const [rendered, setRendered] = useState(false)

  useEffect(() => {
    if (rendered) {
      onRendered(1)
    }
  }, [rendered])

  return (
    <PdfPage bgSrc={commonUtils.getImagePath('PDFDownload/pdf-page-7/PDF-bg.jpg')} no={no}>
      <div className={styles.pdfPage7}>
        <PageHeader
          project={project}
          text={'02.' + t('dashboard.projectDetailPage.primaryAnalysis')}
        />
        <div className={styles.content}>
          <div className={styles.contentTitle}>
            {t('dashboard.projectDetailPage.videoView.subjectiveResponseCurve')}
          </div>
          <div className={styles.chart}>
            <CoreChart
              className={styles.pdfChart}
              detailMetrics={chartProject.detailMetrics}
              options={{
                height: '92%',
                dotSize: 10,
                labelVisible: true,
              }}
              onChartReady={(instance) => {
                setChartInstance(instance as unknown as echarts.ECharts)
              }}
            />
            <div
              className={styles.cover}
              style={{
                width: `calc(100% - ${chartGrid.left + chartGrid.right}px)`,
                marginLeft: chartGrid.left,
              }}
            >
              <VideoAnalysisChartCover
                chartInstance={chartInstance}
                project={chartProject}
                orientation={isPortraitLayout ? 'portrait' : 'landscape'}
                thumbnailMode={
                  isPortraitLayout
                    ? isPortraitSplashCreative
                      ? 'portrait-ppt'
                      : 'portrait-pdf'
                    : 'default'
                }
                onRendered={() => {
                  setRendered(true)
                }}
              />
            </div>
            <div className={styles.legend}>
              <LegendDot
                orientation={isPortraitLayout ? 'portrait' : 'landscape'}
                layout='centered-row'
              />
            </div>
          </div>
        </div>
      </div>
    </PdfPage >
  )
}

export default PdfPage7
